import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const BrandPanel = () => (
  <div className="hidden lg:flex relative flex-col justify-between bg-[#24474A] text-white p-12 overflow-hidden">
    <div className="absolute -right-32 -bottom-32 w-[520px] h-[520px] rounded-full border border-white/10" />
    <div className="absolute -right-10 -bottom-10 w-[340px] h-[340px] rounded-full border border-white/10" />
    <div className="flex items-center gap-3 relative">
      <span className="w-14 h-14 rounded-full bg-white grid place-items-center"><img src="/logo.png" alt="JP Hospitality" className="w-12 h-12 rounded-full object-cover" /></span>
      <span className="flex flex-col"><span className="text-2xl font-bold tracking-wide" data-testid="login-app-name">TSKPRO</span><span className="text-[11px] tracking-[0.3em] uppercase text-white/70" data-testid="login-tagline">Team - Task - Productivity</span></span>
    </div>
    <div className="relative max-w-md">
      <h1 className="text-5xl font-bold leading-[1.05]">Every shift, task and day off — in one calm calendar.</h1>
      <p className="mt-5 text-white/70 text-base">Schedules for every team, reviewed by supervisors and visible to the whole team.</p>
    </div>
    <div className="relative grid grid-cols-3 gap-3 max-w-md text-xs">
      <span className="rounded-xl bg-teal-50 text-teal-900 px-3 py-2 font-bold">Office shift</span>
      <span className="rounded-xl bg-white text-slate-800 border-l-4 border-amber-500 px-3 py-2 font-bold">Task</span>
      <span className="rounded-xl bg-rose-100 text-rose-900 px-3 py-2 font-bold holiday-stripes">Leave</span>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <BrandPanel />
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="lg:hidden flex flex-col items-center gap-1"><img src="/logo.png" alt="JP Hospitality" className="w-24 h-24" /><span className="text-2xl font-bold text-[#24474A]">TSKPRO</span><span className="text-[11px] tracking-[0.3em] uppercase text-slate-500">Team - Task - Productivity</span></div>
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#24474A]">Welcome back</h2>
            <p className="text-slate-500 mt-2">Sign in with the account your admin created for you.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-[#24474A]" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
          </div>
          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full h-11 rounded-full text-base" disabled={busy} data-testid="login-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}
          </Button>
          <p className="text-xs text-slate-400 text-center">No account? Ask your admin to add you.</p>
        </form>
      </div>
    </div>
  );
}
